/**
 * monetization/providers — provider-agnostic payment abstraction (client side).
 *
 * Client NEVER speaks with payment gateway directly: server exposes
 * POST /api/billing/checkout and injected provider decides (Rust trait
 * `PaymentProvider`). This TS mirror describes how UI must INTERPRET
 * active provider: whether it offers automated checkout or upgrade is manual
 * flow (provider "none": applied by admin).
 *
 * Adding a new provider = register its descriptor here + implement trait
 * in Rust. No screens or domain logic changes.
 */

import {
  clampProviderToPlatform,
  isStoreBinaryPlatform,
  normalizePlatform,
  type PurchaseGateReason,
  type ResolvedPurchasePlatform,
  UNKNOWN_PURCHASE_PLATFORM,
} from "./purchase";

export interface CheckoutResult {
  provider: string;
  /** Hosted payment URL to redirect to (if provider offers it). */
  checkoutUrl: string | null;
  /** true ⇒ manual upgrade (contact / applied by admin). */
  manual: boolean;
}

/** Descriptor of a payment provider, seen from UI. */
export interface PaymentProviderDescriptor {
  id: string;
  /** Does upgrade open a hosted checkout? (false ⇒ manual flow). */
  supportsCheckout: boolean;
  /** Does server receive webhooks from this provider? */
  supportsWebhooks: boolean;
}

const NONE_PROVIDER: PaymentProviderDescriptor = {
  id: "none",
  supportsCheckout: false,
  supportsWebhooks: false,
};

const REGISTRY: Record<string, PaymentProviderDescriptor> = {
  none: NONE_PROVIDER,
  stripe: { id: "stripe", supportsCheckout: true, supportsWebhooks: true },
};

/** Registered ids (for Select in admin panel). */
export const PAYMENT_PROVIDER_IDS = Object.keys(REGISTRY);

/** Descriptor for active provider; unknown ⇒ behaves like "none". */
export function getPaymentProvider(id: string | undefined): PaymentProviderDescriptor {
  return REGISTRY[id ?? "none"] ?? NONE_PROVIDER;
}

/**
 * Interprets server checkout response in provider-agnostic manner:
 * returns action UI must execute.
 *
 * `blocked` ⇒ **no navigation occurs**: surface does not admit that
 * purchase (see `monetization/purchase.ts`).
 */
export type CheckoutAction =
  | { kind: "redirect"; url: string }
  | { kind: "manual" }
  | { kind: "blocked"; reason: PurchaseGateReason };

/**
 * **Client's second gate, and the one that truly closes.**
 *
 * `platform` is MANDATORY parameter on purpose. Previous version of
 * this function lacked it and returned `redirect` with any URL arriving
 * from server; `location.assign` on plan screen did the rest.
 * Making it mandatory turns omissions into **type errors** in each
 * clone: no need to remember guard, will not compile without it. (And it is
 * deliberately a type rather than a lint: in this fleet guards that only
 * examined text guarded nothing.)
 *
 * Rules, all fail-closed:
 *   * unclassified surface ⇒ `blocked`;
 *   * server-returned provider illegal on this surface (Stripe in APK) ⇒ `blocked`;
 *   * **store binary ⇒ never navigates to payment URL**, even
 *     with legal provider: Play policy targets *link-out* as well;
 *   * if no URL or flow is manual ⇒ `manual`, as always.
 */
export function interpretCheckout(
  result: CheckoutResult,
  platform: ResolvedPurchasePlatform | string,
): CheckoutAction {
  const surface = normalizePlatform(platform);
  if (surface === UNKNOWN_PURCHASE_PLATFORM) {
    return { kind: "blocked", reason: "unknown-platform" };
  }
  const provider = result.provider.trim().toLowerCase();
  if (clampProviderToPlatform(surface, provider) !== provider) {
    return { kind: "blocked", reason: "provider-not-allowed" };
  }
  if (!result.manual && result.checkoutUrl) {
    if (isStoreBinaryPlatform(surface)) {
      // Belt and suspenders: even if a provider with URL were legal in a
      // store binary one day, outbound navigation is not.
      return { kind: "blocked", reason: "provider-not-allowed" };
    }
    return { kind: "redirect", url: result.checkoutUrl };
  }
  return { kind: "manual" };
}
